import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faSearch,
  faSlidersH,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Fuse from 'fuse.js';

import { useFilterActions, useFilterData, useScheduleData, useViewActions, useViewData } from "../../store/magnetboardStore";
import { faCheckSquare, faSquare } from "@fortawesome/free-regular-svg-icons";
import Dropdown from "../../../Dropdown";
import { startMagnetboardDiagnosticTimer } from "../utils/diagnostics";
import { isNoDepartmentFilterValue } from "../../utils/departmentFilterUtils";
import { getSectionTermFilterInfo } from "../../utils/sectionTermUtils";
import { getCourseCodeFamilyMatches, normalizeCourseCode } from "../utils/courseSearchUtils";

const STUDENT_RANGE_FILTER_KEY = 'student_range';
const UNPLACED_FILTER_VALUE = 'unplaced';
const FILTER_TITLE_LABELS = {
  terms: 'Term',
};

const getSelectedFilterValues = (filterOptions = {}, filterKey) => {
  const options = Array.isArray(filterOptions?.[filterKey]) ? filterOptions[filterKey] : [];
  return options.filter(option => option.selected).map(option => `${option.value}`);
};

const sectionMatchesCourseViewPeriodTermFilters = (section, selectedPeriods = [], selectedTerms = []) => {
  const section_periods = Array.isArray(section?.course_periods) ? section.course_periods.map(period_id => `${period_id}`) : [];

  if(selectedPeriods.length > 0)
  {
    const matches_period = selectedPeriods.some(selected_period => (
      selected_period === UNPLACED_FILTER_VALUE
        ? section_periods.length === 0
        : section_periods.includes(selected_period)
    ));
    if(!matches_period) return false;
  }

  if(selectedTerms.length > 0)
  {
    const section_term_filter_value = `${getSectionTermFilterInfo(section).value}`;
    if(!selectedTerms.some(selected_term => selected_term === section_term_filter_value)) return false;
  }

  return true;
};

const MagnetboardFilters = React.memo(
  ({
    magnetboardOptions,
    viewPreferencesLoaded = false,
    pendingViewSelection = null,
    deferStudentViewWork = false,
    onChangeView = null,
    scheduleData: {
      courses,
      teachers,
      sections,
      classrooms,
      students,
      studentMatchPercentages,
    },
  }) => {
    const { currentView, currentViewType, currentDay } = useViewData();
    const { 
      singletons_filtered,
      doubletons_filtered,
      labs_only_filtered,
      non_labs_only_filtered,
      no_classroom_filtered,
      has_classroom_filtered,
      searchValue,
      filterOptions,
    } = useFilterData();
    const { 
      setFilterIDs,
      setFiltered,
      setSingletonFiltered,
      setDoubletonFiltered,
      setLabsOnlyFiltered,
      setNonLabsOnlyFiltered,
      setNoClassroomFiltered,
      setHasClassroomFiltered,
      setSearchValue,
      setFilteredData,
      setFilterOptions,
      clearFilters,
    } = useFilterActions();
    const {
      displaySchedule,
      scheduleType,
      dayOptions
    } = useScheduleData();
    const {
      changeCurrentView,
      changeCurrentViewType,
      changeCurrentDay
    } = useViewActions();

    const filter_ref = useRef();
    const filter_button_ref = useRef();
    const student_range_picker_refs = useRef({start: null, end: null});
    const student_range_action_timeout_ref = useRef(null);
    const student_range_action_clear_timeout_ref = useRef(null);
    const [filterDataVisible, setFilterDataVisible] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [openFilterOptions, setOpenFilterOptions] = useState([]);
    const [searchInputValue, setSearchInputValue] = useState(searchValue || '');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [studentRangeSearchValues, setStudentRangeSearchValues] = useState({
      start: '',
      end: '',
    });
    const [focusedStudentRangeEndpoint, setFocusedStudentRangeEndpoint] = useState(null);
    const [pendingStudentRangeAction, setPendingStudentRangeAction] = useState(null);

    const viewTypeOptions = useMemo(() => [
      { value: 'period', display: 'Period View' },
      { value: 'day', display: 'Day View' }
    ], []);

    const searchPlaceholder = useMemo(() => {
      switch (currentView) {
        case 'teacher':
          return 'Search for a teacher or course...';
        case 'course':
          return 'Search for a course or teacher...';
        case 'classroom':
          return 'Search for a classroom, teacher, or course...';
        case 'student':
          return 'Search by student name or ID...';
        default:
          return 'Search...';
      }
    }, [currentView]);

    useEffect(() => {
      setSearchInputValue(searchValue || '');
    }, [searchValue]);

    const toggleFilterOptions = () => {
		  setFilterDataVisible(!filterDataVisible);
	  }

    // debounce function helper
    const debounce = useCallback((func, delay) => {
      let timeoutId;
      
      const debouncedFunction = (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
      };
      
      debouncedFunction.cancel = () => {
        clearTimeout(timeoutId);
      };
      
      return debouncedFunction;
    }, []);

    const teacherSearchData = useMemo(() => {
      return teachers.map((teacher) => {
        const lastName = (teacher.name || '').toString().trim();
        const firstName = (teacher.first_name || '').toString().trim();
        return {
          ...teacher,
          full_name: `${firstName} ${lastName}`.trim(),
          full_name_reverse: `${lastName}, ${firstName}`.trim(),
        };
      });
    }, [teachers]);

    const normalizeStudentName = (value) =>
      (value || '')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const normalizeSearchText = useCallback((value) =>
      (value || '')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    , []);

    const textMatchesSearch = useCallback((searchValue, ...fieldValues) => {
      const normalizedSearchValue = normalizeSearchText(searchValue);
      if (!normalizedSearchValue) return false;

      const combinedFieldValue = fieldValues
        .map((fieldValue) => normalizeSearchText(fieldValue))
        .filter(Boolean)
        .join(' ');

      if (!combinedFieldValue) return false;
      if (combinedFieldValue.includes(normalizedSearchValue)) return true;

      const searchTokens = normalizedSearchValue.split(' ').filter(Boolean);
      return searchTokens.length > 0 && searchTokens.every((searchToken) => combinedFieldValue.includes(searchToken));
    }, [normalizeSearchText]);

    const getHighConfidenceFuseMatches = useCallback((results, { maxScore = 0.12, limit = null } = {}) => {
      const filteredResults = results.filter((result) => {
        if (typeof result?.score !== 'number') return true;
        return result.score <= maxScore;
      });

      const limitedResults = limit ? filteredResults.slice(0, limit) : filteredResults;
      return limitedResults.map((result) => result.item);
    }, []);

    const studentSearchData = useMemo(() => {
      return students.map((student) => {
        const firstName = (student.first_name || '').toString().trim();
        const lastName = (student.last_name || '').toString().trim();
        return {
          ...student,
          full_name: `${firstName} ${lastName}`.trim(),
          full_name_reverse: `${lastName}, ${firstName}`.trim(),
          full_name_normalized: normalizeStudentName(`${firstName} ${lastName}`),
          full_name_reverse_normalized: normalizeStudentName(`${lastName} ${firstName}`),
          student_id_normalized: normalizeStudentName(student.student_id),
          student_code_normalized: normalizeStudentName(student.student_code),
          state_id_normalized: normalizeStudentName(student.state_id),
        };
      });
    }, [students]);

    const getStudentRangeDisplayName = useCallback((student = {}) => {
      const firstName = (student.first_name || '').toString().trim();
      const lastName = (student.last_name || '').toString().trim();
      const grade = (student.grade || '').toString().trim();
      const studentName = `${lastName}, ${firstName}`.replace(/^,\s*/, '').replace(/,\s*$/, '').trim();
      return `${studentName || 'Unnamed Student'}${grade ? ` - Grade ${grade}` : ''}`;
    }, []);

    const getStudentRangeNameOnly = useCallback((student = {}) => {
      const firstName = (student.first_name || '').toString().trim();
      const lastName = (student.last_name || '').toString().trim();
      return `${lastName}, ${firstName}`.replace(/^,\s*/, '').replace(/,\s*$/, '').trim() || 'Unnamed Student';
    }, []);

    const getStudentRangeLastNameOnly = useCallback((student = {}) => {
      const lastName = (student.last_name || '').toString().trim();
      return lastName || getStudentRangeNameOnly(student);
    }, [getStudentRangeNameOnly]);

    const normalizeStudentRangeValue = useCallback((value) =>
      (value || '')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    , []);

    const studentRangeStudents = useMemo(() => {
      return students.map((student) => {
        const firstName = (student.first_name || '').toString().trim();
        const lastName = (student.last_name || '').toString().trim();
        const displayName = getStudentRangeDisplayName(student);
        const sortKey = [
          normalizeStudentRangeValue(lastName),
          normalizeStudentRangeValue(firstName),
          `${student.student_id || ''}`,
        ].join('\u0000');

        return {
          ...student,
          range_display_name: displayName,
          range_search_text: normalizeStudentRangeValue(`${displayName} ${firstName} ${lastName}`),
          range_sort_key: sortKey,
        };
      }).sort((studentA, studentB) => studentA.range_sort_key.localeCompare(studentB.range_sort_key));
    }, [getStudentRangeDisplayName, normalizeStudentRangeValue, students]);

    const studentRangeStudentById = useMemo(() => {
      return studentRangeStudents.reduce((results, student) => {
        results[`${student.student_id}`] = student;
        return results;
      }, {});
    }, [studentRangeStudents]);

    const studentRangeIndexById = useMemo(() => {
      return studentRangeStudents.reduce((results, student, index) => {
        results[`${student.student_id}`] = index;
        return results;
      }, {});
    }, [studentRangeStudents]);

    // fuse instances
    const searchEngines = useMemo(() => ({
      teachers: new Fuse(teacherSearchData, {
        keys: ['name', 'first_name', 'full_name', 'full_name_reverse'],
        threshold: .25,
        ignoreLocation: true,
        includeScore: true,
      }),
      courses: new Fuse(courses, {
        keys: ['name', 'course_code'],
        threshold: .2,
        ignoreLocation: true,
        includeScore: true,
      }),
      classrooms: new Fuse(classrooms, {
        keys: ['classroom_name', 'classroom_type'],
        threshold: .2,
        ignoreLocation: true,
        includeScore: true,
      }),
      students: new Fuse(studentSearchData, {
        keys: [
          'first_name',
          'last_name',
          'full_name',
          'full_name_reverse',
          'full_name_normalized',
          'full_name_reverse_normalized',
          'student_id',
          'student_code',
          'state_id',
          'student_id_normalized',
          'student_code_normalized',
          'state_id_normalized',
        ],
        threshold: .2,
        ignoreLocation: true,
        includeScore: true,
      })
    }), [teacherSearchData, courses, classrooms, studentSearchData]);

    // debounced search function
    const debouncedSearch = useMemo(() => {
      return debounce((value) => {
        setDebouncedSearchValue((value || '').trim());
      }, 300);
    }, [debounce]);

    // search logic functions
    const searchTeachers = useCallback((searchValue, engines, sections, teachersList, coursesList) => {
      const normalizedSearchValue = (searchValue || '').toString().trim().toLowerCase();
      if (!normalizedSearchValue) return [];

      // 0) Exact course code match override:
      // if the search exactly matches one or more course codes, only show teachers for those courses.
      const normalizedCourseCodeSearch = normalizeCourseCode(searchValue);
      const exactCourseCodeMatches = coursesList.filter(
        (course) => normalizeCourseCode(course.course_code) === normalizedCourseCodeSearch
      );

      if (exactCourseCodeMatches.length > 0) {
        const exactCourseIDs = new Set(exactCourseCodeMatches.map((course) => course.course_id));
        const exactCourseTeacherIDs = sections.reduce((result, section) => {
          if (exactCourseIDs.has(section.course_id) && section.teacher_id) result.push(section.teacher_id);
          return result;
        }, []);

        return [...new Set(exactCourseTeacherIDs)];
      }

      // 1) High-confidence direct name matching (preferred in teacher view)
      const direct_teacher_matches = teachersList.filter((teacher) => {
        return textMatchesSearch(
          normalizedSearchValue,
          teacher.name,
          teacher.first_name,
          `${teacher.first_name || ''} ${teacher.name || ''}`,
          `${teacher.name || ''}, ${teacher.first_name || ''}`
        );
      });

      if (direct_teacher_matches.length > 0) {
        return direct_teacher_matches.map((teacher) => teacher.teacher_id);
      }

      const direct_course_matches = coursesList.filter((course) =>
        textMatchesSearch(normalizedSearchValue, course.name, course.course_code)
      );

      if (direct_course_matches.length > 0) {
        const directCourseIDs = new Set(direct_course_matches.map((course) => course.course_id));
        const directCourseTeacherIDs = sections.reduce((result, section) => {
          if (directCourseIDs.has(section.course_id) && section.teacher_id) result.push(section.teacher_id);
          return result;
        }, []);

        return [...new Set(directCourseTeacherIDs)];
      }

      const fuse_course_matches = getHighConfidenceFuseMatches(
        engines.courses.search(normalizedSearchValue),
        { maxScore: 0.08, limit: 8 }
      );

      if (fuse_course_matches.length > 0) {
        const fuseCourseIDs = new Set(fuse_course_matches.map((course) => course.course_id));
        const fuseCourseTeacherIDs = sections.reduce((result, section) => {
          if (fuseCourseIDs.has(section.course_id) && section.teacher_id) result.push(section.teacher_id);
          return result;
        }, []);

        if (fuseCourseTeacherIDs.length > 0) {
          return [...new Set(fuseCourseTeacherIDs)];
        }
      }

      // 2) Teacher Fuse fallback only after course matching paths fail.
      return getHighConfidenceFuseMatches(
        engines.teachers.search(normalizedSearchValue),
        { maxScore: 0.12, limit: 10 }
      ).map((teacher) => teacher.teacher_id);
    }, [getHighConfidenceFuseMatches, textMatchesSearch]);

    const searchClassrooms = useCallback((searchValue, engines, sections, teachersList, coursesList, classroomsList) => {
      const normalizedSearchValue = normalizeSearchText(searchValue);
      if (!normalizedSearchValue) return [];

      const addClassroomsForCourseIds = (courseIds, result) => {
        const courseIdSet = new Set(courseIds);
        sections.forEach((section) => {
          if (courseIdSet.has(section.course_id) && section.classroom_id) {
            result.push(section.classroom_id);
          }
        });
      };

      const addClassroomsForTeacherIds = (teacherIds, result) => {
        const teacherIdSet = new Set(teacherIds.map((teacherId) => `${teacherId}`));
        sections.forEach((section) => {
          const hasTeacherMatch = teacherIdSet.has(`${section.teacher_id}`)
            || (Array.isArray(section.secondary_teachers) && section.secondary_teachers.some((teacherId) => teacherIdSet.has(`${teacherId}`)));
          if (hasTeacherMatch && section.classroom_id) {
            result.push(section.classroom_id);
          }
        });
      };

      const directClassroomMatches = classroomsList.filter((classroom) =>
        textMatchesSearch(normalizedSearchValue, classroom.classroom_name, classroom.classroom_type)
      ).map((classroom) => classroom.classroom_id);

      const directCourseMatches = coursesList
        .filter((course) => textMatchesSearch(normalizedSearchValue, course.name, course.course_code))
        .map((course) => course.course_id);

      const directTeacherMatches = teachersList
        .filter((teacher) =>
          textMatchesSearch(
            normalizedSearchValue,
            teacher.name,
            teacher.first_name,
            `${teacher.first_name || ''} ${teacher.name || ''}`,
            `${teacher.name || ''}, ${teacher.first_name || ''}`
          )
        )
        .map((teacher) => teacher.teacher_id);

      const directClassroomIds = [...directClassroomMatches];
      addClassroomsForCourseIds(directCourseMatches, directClassroomIds);
      addClassroomsForTeacherIds(directTeacherMatches, directClassroomIds);

      if (directClassroomIds.length > 0) {
        return [...new Set(directClassroomIds)];
      }

      const fuzzyClassroomIds = getHighConfidenceFuseMatches(
        engines.classrooms.search(normalizedSearchValue),
        { maxScore: 0.08, limit: 6 }
      ).map((classroom) => classroom.classroom_id);

      const fuzzyCourseIds = getHighConfidenceFuseMatches(
        engines.courses.search(normalizedSearchValue),
        { maxScore: 0.08, limit: 6 }
      ).map((course) => course.course_id);

      const fuzzyTeacherIds = getHighConfidenceFuseMatches(
        engines.teachers.search(normalizedSearchValue),
        { maxScore: 0.12, limit: 8 }
      ).map((teacher) => teacher.teacher_id);

      const fuzzyClassroomResults = [...fuzzyClassroomIds];
      addClassroomsForCourseIds(fuzzyCourseIds, fuzzyClassroomResults);
      addClassroomsForTeacherIds(fuzzyTeacherIds, fuzzyClassroomResults);

      return [...new Set(fuzzyClassroomResults)];
    }, [getHighConfidenceFuseMatches, normalizeSearchText, textMatchesSearch]);

    const searchCourses = useCallback((searchValue, engines, sections, teachersList) => {
      const filtered_course_ids = [];
      const normalizedSearchValue = (searchValue || '').toString().trim().toLowerCase();
      if (!normalizedSearchValue) return [];

      const normalizedCourseCodeSearch = normalizeCourseCode(searchValue);
      const exactCourseCodeMatches = getCourseCodeFamilyMatches(courses, normalizedCourseCodeSearch);

      if (exactCourseCodeMatches.length > 0) {
        return [...new Set(exactCourseCodeMatches.map((course) => course.course_id))];
      }

      const directCourseMatches = courses.filter((course) => {
        return textMatchesSearch(normalizedSearchValue, course.name, course.course_code);
      });

      if (directCourseMatches.length > 0) {
        return [...new Set(directCourseMatches.map((course) => course.course_id))];
      }

      getHighConfidenceFuseMatches(
        engines.courses.search(normalizedSearchValue),
        { maxScore: 0.08, limit: 10 }
      ).forEach((course) => {
        filtered_course_ids.push(course.course_id);
      });

      const directTeacherMatches = teachersList.filter((teacher) => {
        return textMatchesSearch(
          normalizedSearchValue,
          teacher.name,
          teacher.first_name,
          `${teacher.first_name || ''} ${teacher.name || ''}`,
          `${teacher.name || ''}, ${teacher.first_name || ''}`
        );
      });

      directTeacherMatches.forEach((teacher) => {
        const teacher_id = teacher.teacher_id;
        const sections_with_teacher = sections.filter((section) => {
          return section.teacher_id === teacher_id ||
            (Array.isArray(section.secondary_teachers) && section.secondary_teachers.includes(teacher_id));
        });

        sections_with_teacher.forEach((section) => {
          if (section.course_id) filtered_course_ids.push(section.course_id);
        });
      });

      getHighConfidenceFuseMatches(
        engines.teachers.search(normalizedSearchValue),
        { maxScore: 0.12, limit: 8 }
      ).forEach((teacher) => {
        const teacher_id = teacher.teacher_id;
        sections.forEach((section) => {
          if (
            section.teacher_id === teacher_id ||
            (Array.isArray(section.secondary_teachers) && section.secondary_teachers.includes(teacher_id))
          ) {
            filtered_course_ids.push(section.course_id);
          }
        });
      });

      return [...new Set(filtered_course_ids)];
    }, [courses, getHighConfidenceFuseMatches, textMatchesSearch]);

    const searchStudents = useCallback((searchValue, engines) => {
      const normalizedSearchValue = normalizeStudentName(searchValue);
      if (!normalizedSearchValue) return [];

      const exact_identifier_matches = studentSearchData
        .filter((student) => [
          student.student_id_normalized,
          student.student_code_normalized,
          student.state_id_normalized,
        ].some((identifier) => identifier && identifier === normalizedSearchValue))
        .map((student) => student.student_id);

      if(exact_identifier_matches.length > 0) return [...new Set(exact_identifier_matches)];

      const direct_identifier_matches = studentSearchData
        .filter((student) => textMatchesSearch(
          normalizedSearchValue,
          student.student_id,
          student.student_code,
          student.state_id,
        ))
        .map((student) => student.student_id);
      const student_results = [...engines.students.search(normalizedSearchValue)];
      const fuzzy_match_ids = student_results.map((result) => result.item.student_id);

      return [...new Set([...direct_identifier_matches, ...fuzzy_match_ids])].slice(0, 30);
    }, [studentSearchData, textMatchesSearch]);

    // filtered search ids
    const filteredIDs = useMemo(() => {
      if (!debouncedSearchValue) return [];
      if (currentView === 'student' && deferStudentViewWork) return [];
      
      switch(currentView) {
        case 'teacher':
          const searchedTeachers = searchTeachers(debouncedSearchValue, searchEngines, sections, teachers, courses);
          return searchedTeachers.length > 0 ? searchedTeachers : false;
        case 'course':
          const searchedCourses = searchCourses(debouncedSearchValue, searchEngines, sections, teachers);
          return searchedCourses.length > 0 ? searchedCourses : false;
        case 'classroom':
          const searchedClassrooms = searchClassrooms(debouncedSearchValue, searchEngines, sections, teachers, courses, classrooms);
          return searchedClassrooms.length > 0 ? searchedClassrooms : false;
        case 'student':
          const searchedStudents = searchStudents(debouncedSearchValue, searchEngines);
          return searchedStudents.length > 0 ? searchedStudents : false;
        default:
          return [];
      }
    }, [debouncedSearchValue, currentView, deferStudentViewWork, searchEngines, sections, teachers, courses, classrooms, searchTeachers, searchCourses, searchClassrooms, searchStudents]);

    // debounce update
    useEffect(() => {
      debouncedSearch(searchInputValue);
    }, [searchInputValue, debouncedSearch]);

    useEffect(() => {
      if (searchValue !== debouncedSearchValue) {
        setSearchValue(debouncedSearchValue);
      }
    }, [debouncedSearchValue, searchValue, setSearchValue]);

    // filtered ids watcher
    useEffect(() => {
      if (currentView === 'student' && deferStudentViewWork) {
        setFilterIDs([]);
        return;
      }

      setFilterIDs(filteredIDs);

      const hasSearch = filteredIDs.length > 0 && debouncedSearchValue.length > 0;
      if(hasSearch) setFiltered(true);
    }, [filteredIDs, currentView, deferStudentViewWork, setFilterIDs, setFiltered, debouncedSearchValue, selectedFilters.length]);

    // clean up timer on unmount
    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    const handleSearchChange = useCallback((e) => {
      setSearchInputValue(e.target.value);
    }, []);

    const updateFilterOptions = (filter_title, option_index, selected) => {
      const singletons_filtered_temp = (filter_title === 'singletons' && filterOptions['singletons'][option_index].value === 'singletons') ? selected : singletons_filtered;
      const doubletons_filtered_temp = (filter_title === 'singletons' && filterOptions['singletons'][option_index].value === 'doubletons') ? selected : doubletons_filtered;
      const labs_only_filtered_temp = (filter_title === 'labs' && filterOptions['labs'][option_index].value === 'labs_only') ? selected : labs_only_filtered;
      const non_labs_only_filtered_temp = (filter_title === 'labs' && filterOptions['labs'][option_index].value === 'non_labs_only') ? selected : non_labs_only_filtered;
      const no_classroom_filtered_temp = (filter_title === 'classroom_status' && filterOptions['classroom_status'][option_index].value === 'no_classroom') ? selected : no_classroom_filtered;
      const has_classroom_filtered_temp = (filter_title === 'classroom_status' && filterOptions['classroom_status'][option_index].value === 'has_classroom') ? selected : has_classroom_filtered;

      if (singletons_filtered_temp !== singletons_filtered) setSingletonFiltered(singletons_filtered_temp);
      if (doubletons_filtered_temp !== doubletons_filtered) setDoubletonFiltered(doubletons_filtered_temp);
      if (labs_only_filtered_temp !== labs_only_filtered) setLabsOnlyFiltered(labs_only_filtered_temp);
      if (non_labs_only_filtered_temp !== non_labs_only_filtered) setNonLabsOnlyFiltered(non_labs_only_filtered_temp);
      if (no_classroom_filtered_temp !== no_classroom_filtered) setNoClassroomFiltered(no_classroom_filtered_temp);
      if (has_classroom_filtered_temp !== has_classroom_filtered) setHasClassroomFiltered(has_classroom_filtered_temp);

      filterOptions[filter_title][option_index]['selected'] = selected;
      setFilterOptions({...filterOptions});
    }

    const getStudentRangeFilterDisplay = useCallback((start_student_id, end_student_id) => {
      const start_student = studentRangeStudentById[`${start_student_id}`];
      const end_student = studentRangeStudentById[`${end_student_id}`];
      if(!start_student || !end_student) return 'Student Range';
      return `${getStudentRangeLastNameOnly(start_student)} - ${getStudentRangeLastNameOnly(end_student)}`;
    }, [getStudentRangeLastNameOnly, studentRangeStudentById]);

    const updateStudentRangeFilter = useCallback((updates = {}) => {
      const current_range_options = filterOptions[STUDENT_RANGE_FILTER_KEY];
      if(!Array.isArray(current_range_options) || current_range_options.length === 0) return;

      const next_filter_options = {...filterOptions};
      const current_option = current_range_options[0] || {};
      const next_option = {...current_option, ...updates};
      const start_student_id = next_option.start_student_id || null;
      const end_student_id = next_option.end_student_id || null;

      next_option.start_student_id = start_student_id;
      next_option.end_student_id = end_student_id;
      next_option.selected = Boolean(start_student_id && end_student_id);
      next_option.display = next_option.selected ? getStudentRangeFilterDisplay(start_student_id, end_student_id) : 'Student Range';

      next_filter_options[STUDENT_RANGE_FILTER_KEY] = [next_option];
      setFilterOptions(next_filter_options);
    }, [filterOptions, getStudentRangeFilterDisplay, setFilterOptions]);

    const clearStudentRangeFilter = useCallback(() => {
      setStudentRangeSearchValues({start: '', end: ''});
      updateStudentRangeFilter({
        start_student_id: null,
        end_student_id: null,
        selected: false,
        display: 'Student Range',
      });
    }, [updateStudentRangeFilter]);

    const runStudentRangeActionWithFeedback = useCallback((endpoint, action_type, action, target_student_id = null) => {
      if(student_range_action_timeout_ref.current) window.clearTimeout(student_range_action_timeout_ref.current);
      if(student_range_action_clear_timeout_ref.current) window.clearTimeout(student_range_action_clear_timeout_ref.current);

      setPendingStudentRangeAction({endpoint, action_type, target_student_id});

      student_range_action_timeout_ref.current = window.setTimeout(() => {
        action();
        student_range_action_clear_timeout_ref.current = window.setTimeout(() => {
          setPendingStudentRangeAction(null);
          student_range_action_clear_timeout_ref.current = null;
        }, 250);
        student_range_action_timeout_ref.current = null;
      }, 0);
    }, []);

    const selectStudentRangeEndpoint = useCallback((endpoint, student_id) => {
      runStudentRangeActionWithFeedback(endpoint, 'select', () => {
        updateStudentRangeFilter({[`${endpoint}_student_id`]: student_id});
        setStudentRangeSearchValues((current_values) => ({
          ...current_values,
          [endpoint]: '',
        }));
        setFocusedStudentRangeEndpoint(null);
      }, student_id);
    }, [runStudentRangeActionWithFeedback, updateStudentRangeFilter]);

    const removeStudentRangeEndpoint = useCallback((endpoint) => {
      runStudentRangeActionWithFeedback(endpoint, 'remove', () => {
        updateStudentRangeFilter({[`${endpoint}_student_id`]: null});
      });
    }, [runStudentRangeActionWithFeedback, updateStudentRangeFilter]);

    const getStudentRangeSearchResults = useCallback((endpoint) => {
      const search_value = normalizeStudentRangeValue(studentRangeSearchValues[endpoint]);
      if(!search_value) return studentRangeStudents;

      return studentRangeStudents.filter((student) => student.range_search_text.includes(search_value));
    }, [normalizeStudentRangeValue, studentRangeSearchValues, studentRangeStudents]);

    const renderStudentRangePicker = useCallback((endpoint, label, selected_student_id) => {
      const selected_student = studentRangeStudentById[`${selected_student_id}`];
      const search_results = getStudentRangeSearchResults(endpoint);
      const results_visible = focusedStudentRangeEndpoint === endpoint;
      const show_search_input = !selected_student || results_visible;
      const pending_endpoint_action = pendingStudentRangeAction?.endpoint === endpoint ? pendingStudentRangeAction?.action_type : null;

      return (
        <div className="mb-student-range-picker" ref={(element) => { student_range_picker_refs.current[endpoint] = element; }} onMouseDown={(event) => event.stopPropagation()}>
          <div className="mb-student-range-label">{label}</div>
          {show_search_input ? (
            <div className="mb-student-range-search-container">
              <input
                className="mb-student-range-search-input click-restricted"
                autoFocus={results_visible}
                value={studentRangeSearchValues[endpoint]}
                onFocus={() => setFocusedStudentRangeEndpoint(endpoint)}
                onChange={(event) => {
                  const next_value = event.target.value;
                  setStudentRangeSearchValues((current_values) => ({
                    ...current_values,
                    [endpoint]: next_value,
                  }));
                }}
                placeholder={selected_student ? getStudentRangeNameOnly(selected_student) : 'Search students'}
              />
              <FontAwesomeIcon className="mb-student-range-search-icon" icon={faSearch}/>
            </div>
          ) : (
            <div
              className="mb-student-range-selected"
              onClick={() => {
                setStudentRangeSearchValues((current_values) => ({
                  ...current_values,
                  [endpoint]: '',
                }));
                setFocusedStudentRangeEndpoint(endpoint);
              }}
            >
              <div>{getStudentRangeNameOnly(selected_student)}</div>
              {pending_endpoint_action === 'remove' ? (
                <FontAwesomeIcon className="mb-student-range-spinner" icon={faSpinner} spin/>
              ) : (
                <FontAwesomeIcon
                  className="dark-gray-link"
                  icon={faTimes}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    removeStudentRangeEndpoint(endpoint);
                  }}
                  onClick={(event) => event.stopPropagation()}
                />
              )}
            </div>
          )}
          {results_visible &&
            <div className="mb-student-range-results">
              {search_results.map((student) => {
                const student_selected = `${student.student_id}` === `${selected_student_id}`;
                return (
                  <div
                    className={`mb-student-range-result click-restricted ${student_selected ? 'mb-student-range-result-selected' : ''}`}
                    key={`${endpoint}-${student.student_id}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      selectStudentRangeEndpoint(endpoint, student.student_id);
                    }}
                  >
                    {pending_endpoint_action === 'select' && `${student.student_id}` === `${pendingStudentRangeAction?.target_student_id}` ? (
                      <div className="mb-student-range-result-loading">
                        <FontAwesomeIcon className="mb-student-range-spinner" icon={faSpinner} spin/>
                        <span>Selecting...</span>
                      </div>
                    ) : (
                      <>
                        <div className="mb-student-range-result-name">{getStudentRangeNameOnly(student)}</div>
                        {student.grade &&
                          <div className="mb-student-range-result-meta">Grade {student.grade}</div>
                        }
                      </>
                    )}
                  </div>
                );
              })}
              {search_results.length === 0 &&
                <div className="mb-student-range-empty">No students found</div>
              }
            </div>
          }
        </div>
      );
    }, [focusedStudentRangeEndpoint, getStudentRangeNameOnly, getStudentRangeSearchResults, pendingStudentRangeAction, removeStudentRangeEndpoint, selectStudentRangeEndpoint, studentRangeSearchValues, studentRangeStudentById]);

    const renderStudentRangeFilter = useCallback((filter_options) => {
      const range_option = Array.isArray(filter_options) ? (filter_options[0] || {}) : {};
      return (
        <div className="mb-student-range-filter click-restricted">
          <div className="mb-student-range-pickers">
            {renderStudentRangePicker('start', 'Start', range_option.start_student_id)}
            {renderStudentRangePicker('end', 'End', range_option.end_student_id)}
          </div>
        </div>
      );
    }, [renderStudentRangePicker]);

    const updateOpenFilters = (filter_title) => {
      const filter_index = openFilterOptions.findIndex(filter => filter === filter_title);
      
      if(filter_index !== -1)
      {
        openFilterOptions.splice(filter_index, 1);
      }
      else
      {
        openFilterOptions.push(filter_title);
      }

      setOpenFilterOptions([...openFilterOptions]);
    }

    // Update filtered data
    useEffect(() => {
      if(!displaySchedule || displaySchedule.length === 0) return;
      const endTimer = startMagnetboardDiagnosticTimer('filters-update-filtered-data', {
        current_view: currentView,
        display_rows: displaySchedule.length,
        filter_groups: Object.keys(filterOptions).length,
        sections: sections.length,
        search_length: debouncedSearchValue.length,
        defer_student_view_work: deferStudentViewWork,
      });

      let selected_filters = [];
      Object.keys(filterOptions).forEach(filter_title => {
        const filter_options = filterOptions[filter_title];
  
        filter_options.forEach(filter_option => {
          if(filter_option.selected) selected_filters.push(filter_option);
        });
      });
      setSelectedFilters(selected_filters)

      if (currentView === 'student' && deferStudentViewWork) {
        setFilteredData([]);
        setFiltered(selected_filters.length > 0 || debouncedSearchValue.length > 0);
        endTimer({deferred: true, selected_filters: selected_filters.length});
        return;
      }

      const hasFiltersApplied = selected_filters.length > 0;
      setFiltered(hasFiltersApplied);
      const selected_period_filters = getSelectedFilterValues(filterOptions, 'periods');
      const selected_term_filters = getSelectedFilterValues(filterOptions, 'terms');
  
      let filtered_data = displaySchedule.reduce((result, display_data) => {
        let include = 0;
        let include_for_department = filterOptions.departments ? 0 : 1;
        let include_specific_data = 0;
        let include_for_course_view_course = filterOptions.courses ? 0 : 1;
        let include_for_course_view_teacher = filterOptions.teachers ? 0 : 1;
        let include_for_classrooms = filterOptions.classrooms ? 0 : 1;
        let include_for_periods = filterOptions.periods ? 0 : 1;
        let include_for_terms = filterOptions.terms ? 0 : 1;
        let include_for_grade = 0;
        let include_for_status = 0;
        let include_for_special_ed = 0;
        let include_for_teams = filterOptions.teams ? 0 : 1;
        let include_for_courses = filterOptions.courses ? 0 : 1;
        let include_for_student_range = filterOptions[STUDENT_RANGE_FILTER_KEY] ? 0 : 1;
        let include_for_course_view_period_term_combo = 1;

        if(currentView === 'course' && (selected_period_filters.length > 0 || selected_term_filters.length > 0))
        {
          const course_id = display_data.display_info.course_id;
          include_for_course_view_period_term_combo = sections.some(section => (
            `${section.course_id}` === `${course_id}` &&
            sectionMatchesCourseViewPeriodTermFilters(section, selected_period_filters, selected_term_filters)
          )) ? 1 : 0;
        }
  
        Object.keys(filterOptions).forEach(filter_title => {
          const filter_options = filterOptions[filter_title];
  
          // If none of the options are selected, then automatically include all of them
          if(filter_options.filter(option => option.selected).length === 0)
          {
            if(filter_title === 'departments') 
            {
              include_for_department = 1;
            }
            else if(currentView === 'course' && filter_title === 'courses')
            {
              include_for_course_view_course = 1;
            }
            else if(currentView === 'course' && filter_title === 'teachers')
            {
              include_for_course_view_teacher = 1;
            }
            else if(currentView === 'teacher' && filter_title === 'teachers')
            {
              include_specific_data = 1;
            }
            else if(currentView === 'teacher' && filter_title === 'classrooms')
            {
              include_for_classrooms = 1;
            }
            else if(currentView === 'course' && filter_title === 'classrooms')
            {
              include_for_classrooms = 1;
            }
            else if(currentView === 'course' && filter_title === 'periods')
            {
              include_for_periods = 1;
            }
            else if(currentView === 'course' && filter_title === 'terms')
            {
              include_for_terms = 1;
            }
            else if(currentView === 'classroom' && filter_title === 'classrooms')
            {
              include_specific_data = 1;
            }
            else if(filter_title === 'grades')
            {
              include_for_grade = 1;
            }
            else if(filter_title === 'status')
            {
              include_for_status = 1;
            }
            else if(filter_title === 'special ed')
            {
              include_for_special_ed = 1;
            }
            else if(filter_title === 'teams')
            {
              include_for_teams = 1;
            }
            else if(filter_title === 'courses')
            {
              include_for_courses = 1;
            }
            else if(filter_title === STUDENT_RANGE_FILTER_KEY)
            {
              include_for_student_range = 1;
            }
          }
          
          // Loop through all filter options and start filtering those that apply
          filter_options.forEach(filter_option => {
            const filter_option_selected = filter_option.selected;
            if(!filter_option_selected) return;
    
            const filter_option_value = filter_option.value;
            const display_departments = Array.isArray(display_data.display_info.departments) ? display_data.display_info.departments : [];
            const department_filter_matches = filter_title === 'departments'
              && (
                display_departments.includes(filter_option_value)
                || (isNoDepartmentFilterValue(filter_option_value) && display_departments.length === 0)
              );
    
            if(currentView === 'teacher')
            {
              if(department_filter_matches) include_for_department = 1;
              if(filter_title === 'teachers' && `${display_data.display_info.teacher_id}` === `${filter_option_value}`) include_specific_data = 1;
  
              if(filter_title === 'classrooms')
              {
                const teacher_id = display_data.display_info.teacher_id;
                const section_teacher_classroom_index = sections.findIndex(section => section.teacher_id === teacher_id && section.classroom_id === filter_option_value);
                if(section_teacher_classroom_index !== -1) include_for_classrooms = 1;
              }
            }
            else if(currentView === 'course')
            {
              if(department_filter_matches) include_for_department = 1;
              if(filter_title === 'courses' && `${display_data.display_info.course_id}` === `${filter_option_value}`) include_for_course_view_course = 1;

              if(filter_title === 'teachers')
              {
                const course_id = display_data.display_info.course_id;
                const section_course_teacher_index = sections.findIndex(section => {
                  const primary_match = `${section.course_id}` === `${course_id}` && `${section.teacher_id}` === `${filter_option_value}`;
                  const secondary_match = `${section.course_id}` === `${course_id}` && Array.isArray(section.secondary_teachers) && section.secondary_teachers.map((teacher_id) => `${teacher_id}`).includes(`${filter_option_value}`);
                  return primary_match || secondary_match;
                });
                if(section_course_teacher_index !== -1) include_for_course_view_teacher = 1;
              }

              if(filter_title === 'classrooms')
              {
                const course_id = display_data.display_info.course_id;
                const section_course_classroom_index = sections.findIndex(section => `${section.course_id}` === `${course_id}` && `${section.classroom_id}` === `${filter_option_value}`);
                if(section_course_classroom_index !== -1) include_for_classrooms = 1;
              }

              if(filter_title === 'periods')
              {
                const course_id = display_data.display_info.course_id;
                const section_course_period_index = sections.findIndex(section => {
                  if(`${section.course_id}` !== `${course_id}`) return false;
                  const section_periods = Array.isArray(section.course_periods) ? section.course_periods.map(period_id => `${period_id}`) : [];
                  return `${filter_option_value}` === UNPLACED_FILTER_VALUE
                    ? section_periods.length === 0
                    : section_periods.includes(`${filter_option_value}`);
                });
                if(section_course_period_index !== -1) include_for_periods = 1;
              }

              if(filter_title === 'terms')
              {
                const course_id = display_data.display_info.course_id;
                const section_course_term_index = sections.findIndex(section => {
                  if(`${section.course_id}` !== `${course_id}`) return false;
                  return `${getSectionTermFilterInfo(section).value}` === `${filter_option_value}`;
                });
                if(section_course_term_index !== -1) include_for_terms = 1;
              }
            }
            else if(currentView === 'classroom')
            {
              if(department_filter_matches) include_for_department = 1;
  
              if(filter_title === 'classrooms' && `${display_data.display_info.classroom_id}` === `${filter_option_value}`) include_specific_data = 1;
            }
            else if(currentView === 'student')
            {
              if(filter_title === 'grades' && display_data.display_info.grade === filter_option_value) include_for_grade = 1;
              
              if(filter_title === 'status')
              {
                const student_id = display_data.data_id;
                const student_match_percent = (student_id in studentMatchPercentages) ? studentMatchPercentages[student_id].match_percent : 0;
                const student_percent_schedule_full =  (student_id in studentMatchPercentages) ? studentMatchPercentages[student_id].percent_schedule_full : 0;
                const best_student_match = Math.max(student_match_percent, student_percent_schedule_full);
  
                const complete = (best_student_match >= 100);
                const mostly_complete = (best_student_match >= 75 && best_student_match < 100);
                const needs_work = (best_student_match < 75);
  
                if(filter_option_value === '1' && complete)
                {
                  include_for_status = 1;
                }
                else if(filter_option_value === '.75' && !complete && mostly_complete)
                {
                  include_for_status = 1;
                }
                else if(filter_option_value === '.5' && !complete && !mostly_complete && needs_work)
                {
                  include_for_status = 1;
                }
              }
  
              if(filter_title === 'special ed' && display_data.display_info.is_special_ed === filter_option_value) include_for_special_ed = 1;

              if(filter_title === 'teams')
              {
                const student_labels = Array.isArray(display_data.display_info.student_labels) ? display_data.display_info.student_labels : [];
                const student_has_team = student_labels.findIndex((student_label) => `${student_label?.label_id}` === `${filter_option_value}`) !== -1;
                if(student_has_team) include_for_teams = 1;
              }
  
              if(filter_title === 'courses')
              {
                const student_id = display_data.data_id;
                const section_student_course_index = sections.findIndex(section => section.course_id === filter_option_value && section.student_list.includes(student_id));
                if(section_student_course_index !== -1) include_for_courses = 1;
              }

              if(filter_title === STUDENT_RANGE_FILTER_KEY)
              {
                const start_index = studentRangeIndexById[`${filter_option.start_student_id}`];
                const end_index = studentRangeIndexById[`${filter_option.end_student_id}`];
                const student_index = studentRangeIndexById[`${display_data.data_id}`];

                if(typeof start_index === 'number' && typeof end_index === 'number' && typeof student_index === 'number')
                {
                  const lower_index = Math.min(start_index, end_index);
                  const upper_index = Math.max(start_index, end_index);
                  if(student_index >= lower_index && student_index <= upper_index) include_for_student_range = 1;
                }
              }
            }
          });
        });
  
        if(currentView === 'teacher' && include_for_department === 1 && include_specific_data === 1 && include_for_classrooms === 1) include = 1;
        if(currentView === 'course' && include_for_department === 1 && include_for_course_view_course === 1 && include_for_course_view_teacher === 1 && include_for_classrooms === 1 && include_for_periods === 1 && include_for_terms === 1 && include_for_course_view_period_term_combo === 1) include = 1;
        if(currentView === 'classroom' && include_for_department === 1 && include_specific_data === 1) include = 1;
        if(currentView === 'student' && include_for_grade === 1 && include_for_status === 1 && include_for_special_ed === 1 && include_for_teams === 1 && include_for_courses === 1 && include_for_student_range === 1) include = 1;
  
        if(include === 1) result.push(display_data.data_id);
  
        return result;
      }, []);

      setFilteredData([...filtered_data]);
      endTimer({
        selected_filters: selected_filters.length,
        filtered_rows: filtered_data.length,
      });
    }, [filterOptions, currentView, deferStudentViewWork, debouncedSearchValue.length, displaySchedule, sections, setFilteredData, studentMatchPercentages, teachers, classrooms, students, setFiltered, studentRangeIndexById]);

    // Outside click handler - only attach when filter is visible
    useEffect(() => {
      if (!filterDataVisible) return;

      const handleClickOutside = (event) => {
        if (filter_ref.current && !filter_ref.current.contains(event.target)) {
          setFilterDataVisible(false);
          setFocusedStudentRangeEndpoint(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [filterDataVisible]);

    useEffect(() => {
      if(filterDataVisible) return;
      setFocusedStudentRangeEndpoint(null);
      setOpenFilterOptions([]);
    }, [filterDataVisible]);

    useEffect(() => {
      return () => {
        if(student_range_action_timeout_ref.current) window.clearTimeout(student_range_action_timeout_ref.current);
        if(student_range_action_clear_timeout_ref.current) window.clearTimeout(student_range_action_clear_timeout_ref.current);
      };
    }, []);

    useEffect(() => {
      if(!focusedStudentRangeEndpoint) return;

      const handleStudentRangeOutsideClick = (event) => {
        const active_picker_ref = student_range_picker_refs.current[focusedStudentRangeEndpoint];
        if(active_picker_ref && active_picker_ref.contains(event.target)) return;
        setFocusedStudentRangeEndpoint(null);
      };

      document.addEventListener('mousedown', handleStudentRangeOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleStudentRangeOutsideClick);
      };
    }, [focusedStudentRangeEndpoint]);

    return (
      <>
        <div className="mb-options-bar">
          <div className="mb-search-bar-outer-container">
            <div className="mb-search-bar-container">
              <input
                className="mb-search-bar"
                placeholder={searchPlaceholder}
                value={searchInputValue}
                onChange={handleSearchChange}
              />
              <FontAwesomeIcon className="mb-search-bar-icon" icon={faSearch} />
              <div className="mb-search-bar-filter-container">
                <div
                  ref={filter_button_ref}
                  style={{
                    display: "grid",
                    width: "100%",
                    height: "100%",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                  onClick={
                    magnetboardOptions.allow_filtering
                      ? toggleFilterOptions
                      : () => {}
                  }
                >
                  <FontAwesomeIcon icon={faSlidersH} />
                </div>
                {filterDataVisible && (
                  <div
                    ref={filter_ref}
                    className="list-filter-options-container click-restricted mb-magnetboard-filter-options-container"
                    style={{ top: "60px" }}
                  >
                    <div className="small-text">Filter by:</div>
                    {Object.keys(filterOptions).map((filter_title, j) => {
                      const formatted_filter_title = FILTER_TITLE_LABELS[filter_title] || filter_title
                        .split("_")
                        .join(" ");
                      const filter_options = filterOptions[filter_title];
                      const filter_options_open =
                        openFilterOptions.includes(filter_title);
                      const filter_group_selected_count = filter_options.filter((filter_option) => filter_option.selected).length;

                      return (
                        <div key={j}>
                          <div
                            className="list-filter-title-container"
                            onClick={() =>
                              updateOpenFilters(
                                filter_title,
                                filter_options_open
                              )
                            }
                          >
                            <div className="capitalize list-filter-title-label">
                              {formatted_filter_title}
                              {filter_group_selected_count > 0 &&
                                <span className="list-filter-title-selected-indicator">{filter_group_selected_count}</span>
                              }
                            </div>
                            <FontAwesomeIcon
                              className="small-text"
                              icon={
                                filter_options_open
                                  ? faChevronUp
                                  : faChevronDown
                              }
                            />
                          </div>
                          {filter_options_open && filter_title === STUDENT_RANGE_FILTER_KEY &&
                            renderStudentRangeFilter(filter_options)
                          }
                          {filter_options_open && filter_title !== STUDENT_RANGE_FILTER_KEY &&
                            filter_options.map((filter_option, i) => {
                              const filter_display = filter_option.display;
                              const filter_selected = filter_option.selected;

                              if(filter_option.is_header)
                              {
                                return (
                                  <div className="mb-filter-option-group-header" key={i}>
                                    {filter_display}
                                  </div>
                                );
                              }

                              return (
                                <div
                                  className="parent-div list-filter-option click-restricted"
                                  key={i}
                                  onClick={() => updateFilterOptions(filter_title, i, !filter_selected)}
                                >
                                  <div className="list-filter-option-name click-restricted">
                                    {filter_display}
                                  </div>
                                  {filter_selected && (
                                    <FontAwesomeIcon
                                      className="fas-checkbox-checked click-restricted"
                                      icon={faCheckSquare}
                                    />
                                  )}
                                  {!filter_selected && (
                                    <FontAwesomeIcon
                                      className="fas-checkbox-unchecked click-restricted"
                                      icon={faSquare}
                                    />
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mb-change-view-container">
            {scheduleType && scheduleType !== "daily" && (
              <>
                <div className="mb-dropdown-wrapper">
                  <Dropdown
                    data={viewTypeOptions}
                    currentValue={currentViewType}
                    handleChange={(new_view_type) =>
                      changeCurrentViewType(new_view_type)
                    }
                  />
                </div>
                {currentViewType === "day" && (
                  <div className="mb-dropdown-wrapper">
                    <Dropdown
                      data={dayOptions}
                      currentValue={currentDay}
                      handleChange={(new_day) => changeCurrentDay(new_day)}
                    />
                  </div>
                )}
              </>
            )}
            <div className="mb-dropdown-wrapper">
              <Dropdown
                data={magnetboardOptions.view_options}
                currentValue={viewPreferencesLoaded ? (pendingViewSelection || currentView) : null}
                displayDefault="--"
                handleChange={(new_view) => (onChangeView || changeCurrentView)(new_view)}
              />
            </div>
          </div>
        </div>
        <div className="mb-selected-filters-bar">
          {selectedFilters.length > 0 && (
            <>
              <div className="mb-selected-filters-text">Filters:</div>
              {Object.keys(filterOptions).map((filter_title, i) => {
                const filter_options = filterOptions[filter_title];
                return (
                  <React.Fragment key={i}>
                    {filter_options.map((filter_option, i) => {
                      const filter_option_selected = filter_option.selected;
                      if (!filter_option_selected) return null;

                      if(filter_title === STUDENT_RANGE_FILTER_KEY)
                      {
                        return (
                          <div className="mb-selected-filters-filter-tag" key={i}>
                            <div className="mb-selected-filters-filter-tag-inner-container">
                              <div>{filter_option.display}</div>
                              <FontAwesomeIcon
                                className="dark-gray-link"
                                icon={faTimes}
                                onClick={clearStudentRangeFilter}
                              />
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="mb-selected-filters-filter-tag" key={i}>
                          <div className="mb-selected-filters-filter-tag-inner-container">
                            <div>{filter_option.display}</div>
                            <FontAwesomeIcon
                              className="dark-gray-link"
                              icon={faTimes}
                              onClick={() =>
                                updateFilterOptions(filter_title, i, false)
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <div className="blue-link small-text" onClick={clearFilters}>
                Clear all filters
              </div>
            </>
          )}
        </div>
      </>
    );
  }
);

export default MagnetboardFilters;
